/**
 * Formateo y lectura de montos para la interfaz y los documentos legales:
 * quetzales con símbolo, porcentajes, conversión desde formularios y montos
 * en letras para pagarés y recibos.
 */
import { CENTS_PER_QUETZAL } from './money.js';
/** Configuración regional de Guatemala para `Intl`. */
const GUATEMALA_LOCALE = 'es-GT';
/** Código ISO 4217 del quetzal. */
const QUETZAL_CURRENCY_CODE = 'GTQ';
/** Los montos siempre se muestran con dos decimales (centavos). */
const MONEY_FRACTION_DIGITS = 2;
/** Puntos básicos por punto porcentual (100 pb = 1 %). */
const BASIS_POINTS_PER_PERCENT = 100;
/**
 * Decimales intermedios al multiplicar un decimal por 100: `toFixed` con este
 * margen elimina la deriva binaria (1411.70 × 100 = 141169.99999…) antes de
 * redondear.
 */
const FLOAT_CLEANUP_DIGITS = 4;
/** Umbral a partir del cual el número se escribe con decenas ("TREINTA Y …"). */
const FIRST_COMPOSED_TEN = 30;
/** Divisores para separar millones, miles y centenas al escribir en letras. */
const ONE_MILLION = 1_000_000;
const ONE_THOUSAND = 1000;
const ONE_HUNDRED = 100;
const TEN = 10;
/** Formateador de quetzales con símbolo de moneda. */
const quetzalFormatter = new Intl.NumberFormat(GUATEMALA_LOCALE, {
    style: 'currency',
    currency: QUETZAL_CURRENCY_CODE,
    minimumFractionDigits: MONEY_FRACTION_DIGITS,
    maximumFractionDigits: MONEY_FRACTION_DIGITS,
});
/** Formateador numérico sin símbolo de moneda. */
const plainNumberFormatter = new Intl.NumberFormat(GUATEMALA_LOCALE, {
    minimumFractionDigits: MONEY_FRACTION_DIGITS,
    maximumFractionDigits: MONEY_FRACTION_DIGITS,
});
/**
 * Formatea centavos como quetzales para mostrar.
 * @example formatMoney(141170) → "Q1,411.70"
 */
export function formatMoney(amount) {
    return quetzalFormatter.format(amount / CENTS_PER_QUETZAL);
}
/** Formatea centavos sin el símbolo de moneda: `141170 → "1,411.70"`. */
export function formatAmount(amount) {
    return plainNumberFormatter.format(amount / CENTS_PER_QUETZAL);
}
/** Formatea puntos básicos como porcentaje: `2400 → "24.00 %"`. */
export function formatRate(rate, fractionDigits = MONEY_FRACTION_DIGITS) {
    return `${(rate / BASIS_POINTS_PER_PERCENT).toFixed(fractionDigits)} %`;
}
/** Convierte un monto decimal (como se teclea en un formulario) a centavos sin deriva de flotantes. */
export function decimalToCents(amount) {
    if (!Number.isFinite(amount))
        throw new TypeError('Monto inválido');
    return Math.round(Number((amount * CENTS_PER_QUETZAL).toFixed(FLOAT_CLEANUP_DIGITS)));
}
/** Convierte centavos a número decimal; solo para valores por defecto de formularios. */
export function centsToDecimal(amount) {
    return amount / CENTS_PER_QUETZAL;
}
/** Convierte un porcentaje tecleado por el usuario (24.5) a puntos básicos (2450). */
export function percentToBasisPoints(percent) {
    if (!Number.isFinite(percent))
        throw new TypeError('Porcentaje inválido');
    return Math.round(Number((percent * BASIS_POINTS_PER_PERCENT).toFixed(FLOAT_CLEANUP_DIGITS)));
}
/** Caracteres que se ignoran al leer montos: símbolo "Q", espacios y separador de miles. */
const MONEY_INPUT_NOISE = /[Qq\s,]/g;
/** Monto decimal válido: signo opcional y hasta dos decimales. */
const MONEY_INPUT_PATTERN = /^-?\d+(\.\d{1,2})?$/;
/**
 * Interpreta un monto tecleado tolerando "Q", espacios y separadores de miles.
 * Devuelve `null` cuando el texto no es un monto válido.
 * @example parseMoneyInput('Q 1,500.50') → 150050
 */
export function parseMoneyInput(text) {
    const normalized = text.replace(MONEY_INPUT_NOISE, '');
    if (!MONEY_INPUT_PATTERN.test(normalized))
        return null;
    return decimalToCents(Number(normalized));
}
/**
 * Escribe un monto en letras, como exigen los pagarés y recibos.
 * @example amountInWords(150050) → "UN MIL QUINIENTOS QUETZALES CON 50/100"
 */
export function amountInWords(amount) {
    const quetzales = Math.floor(Math.abs(amount) / CENTS_PER_QUETZAL);
    const centavos = Math.abs(amount) % CENTS_PER_QUETZAL;
    const words = quetzales === 0 ? 'CERO' : apocopate(integerToSpanishWords(quetzales));
    const currency = quetzales === 1 ? 'QUETZAL' : 'QUETZALES';
    return `${words} ${currency} CON ${String(centavos).padStart(MONEY_FRACTION_DIGITS, '0')}/100`;
}
/** Palabras del 0 al 29 (en español estos no se componen con "Y"). */
const UNITS = [
    '', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE',
    'DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE',
    'DIECIOCHO', 'DIECINUEVE', 'VEINTE', 'VEINTIUNO', 'VEINTIDÓS', 'VEINTITRÉS',
    'VEINTICUATRO', 'VEINTICINCO', 'VEINTISÉIS', 'VEINTISIETE', 'VEINTIOCHO', 'VEINTINUEVE',
];
/** Decenas a partir de treinta (índice = dígito de las decenas). */
const TENS = ['', '', '', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
/** Centenas (índice = dígito de las centenas); "CIEN" exacto se trata aparte. */
const HUNDREDS = [
    '', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS',
    'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS',
];
/** Palabras para un entero entre 1 y 999. */
function belowOneThousand(n) {
    if (n === ONE_HUNDRED)
        return 'CIEN';
    const hundreds = Math.floor(n / ONE_HUNDRED);
    const rest = n % ONE_HUNDRED;
    const parts = [];
    if (hundreds > 0)
        parts.push(HUNDREDS[hundreds] ?? '');
    if (rest > 0 && rest < FIRST_COMPOSED_TEN)
        parts.push(UNITS[rest] ?? '');
    if (rest >= FIRST_COMPOSED_TEN) {
        const unit = rest % TEN;
        const tens = TENS[Math.floor(rest / TEN)] ?? '';
        parts.push(unit === 0 ? tens : `${tens} Y ${UNITS[unit] ?? ''}`);
    }
    return parts.join(' ');
}
/** Palabras en español (forma larga) para un entero no negativo (hasta miles de millones). */
export function integerToSpanishWords(n) {
    if (n === 0)
        return 'CERO';
    const millions = Math.floor(n / ONE_MILLION);
    const thousands = Math.floor((n % ONE_MILLION) / ONE_THOUSAND);
    const rest = n % ONE_THOUSAND;
    const parts = [];
    if (millions > 0) {
        parts.push(millions === 1 ? 'UN MILLÓN' : `${apocopate(integerToSpanishWords(millions))} MILLONES`);
    }
    if (thousands > 0) {
        parts.push(thousands === 1 ? 'UN MIL' : `${apocopate(belowOneThousand(thousands))} MIL`);
    }
    if (rest > 0)
        parts.push(belowOneThousand(rest));
    return parts.join(' ');
}
/** Apócope antes de sustantivo: "VEINTIUNO MIL" → "VEINTIÚN MIL", "UNO" → "UN". */
function apocopate(words) {
    return words.replace(/VEINTIUNO$/, 'VEINTIÚN').replace(/UNO$/, 'UN');
}
//# sourceMappingURL=format.js.map